// Tilbakemeldinger (/tilbakemeldinger, tannhjulmenyen, leder og utvikler; vinnsel 04.11): lederne
// melder feil, forbedringer og ideer til ansatt-appen mens vi utvikler den videre. De kan si seg
// enige i hverandres innspill og kommentere. Utviklerne (hardkodet, src/employeeAccess.js) setter status (under
// utvikling, ferdig utviklet …) med en melding til lederne, og kommentarene deres er merket. Siden
// lederen kom fra fylles inn av tannhjulmenyen (location.state.fra). Utviklerne får varsel i
// varslingssenteret om nye innspill og kommentarer; varselet lenker hit med ?vis=<id>, som viser
// innspillet og merker det som sett (src/lib/tilbakemeldingVarsel.js). Backenden sjekker tilgangen
// (src/routes/tilbakemeldinger.js).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Tilbake, Sidetopp, Tekstfelt, feltKlasse, knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { Melding } from './tjenester/Velgere';
import { useErLeder, useErUtvikler } from '../employeeAccess';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

async function kall(sti, metode = 'GET', data) {
  const res = await fetch(`${API_BASE_URL}/${sti}`, {
    method: metode,
    headers: {
      'X-Ansatt': encodeURIComponent(Cookies.get('selectedEmployee') || ''),
      ...(data ? { 'Content-Type': 'application/json' } : {}),
    },
    body: data ? JSON.stringify(data) : undefined,
    cache: 'no-store',
  });
  const svar = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(svar.message || 'Noe gikk galt. Prøv igjen.');
  return svar;
}

const TYPE = {
  feil: { tekst: 'Feil', hjelp: 'Noe virker ikke som det skal', merke: 'bg-red-100 text-red-900' },
  forbedring: { tekst: 'Forbedring', hjelp: 'Noe som finnes, men kan bli bedre', merke: 'bg-sky-100 text-sky-900' },
  ide: { tekst: 'Idé', hjelp: 'En ny løsning eller funksjon', merke: 'bg-violet-100 text-violet-900' },
};
const STATUS = {
  ny: { tekst: 'Ny', merke: 'bg-amber-100 text-amber-950' },
  vurderes: { tekst: 'Vurderes', merke: 'bg-slate-100 text-slate-800' },
  planlagt: { tekst: 'Planlagt', merke: 'bg-indigo-100 text-indigo-900' },
  pagar: { tekst: 'Under utvikling', merke: 'bg-sky-100 text-sky-900' },
  utviklet: { tekst: 'Ferdig utviklet', hjelp: 'venter på test eller publisering', merke: 'bg-teal-100 text-teal-900' },
  ferdig: { tekst: 'Tatt i bruk', merke: 'bg-emerald-100 text-emerald-900' },
  avvist: { tekst: 'Blir ikke gjort', merke: 'bg-slate-200 text-slate-800' },
};
const VIKTIGHET = {
  lav: { tekst: 'Kan vente', merke: 'bg-slate-100 text-slate-800' },
  middels: { tekst: 'Bør gjøres snart', merke: 'bg-amber-100 text-amber-950' },
  hoy: { tekst: 'Hindrer arbeidet', merke: 'bg-red-100 text-red-900' },
};
const OMRADE = {
  kontrollpanel: 'Kontrollpanel og meny',
  kunder: 'Kunder og kundekort',
  verksted: 'Verksted',
  booking: 'Booking',
  ordre: 'Ordre',
  trykk: 'Tekstiltrykk',
  pristilbud: 'Pristilbud',
  meldinger: 'SMS, e-post og varsler',
  klubb: 'Klubb',
  hotell: 'Dekk- og sykkelhotell',
  nettbutikk: 'Nettbutikk',
  ansatte: 'Ansatte',
  kundeportaler: 'Kundeportalene og Din side',
  annet: 'Annet',
};
const VISNING = {
  apne: { tekst: 'Åpne', filter: (t) => !['ferdig', 'avvist'].includes(t.status) },
  utvikling: { tekst: 'Under utvikling', filter: (t) => ['pagar', 'utviklet'].includes(t.status) },
  ferdig: { tekst: 'Tatt i bruk', filter: (t) => t.status === 'ferdig' },
  avvist: { tekst: 'Blir ikke gjort', filter: (t) => t.status === 'avvist' },
  alle: { tekst: 'Alle', filter: () => true },
};
const MAKS_STANDARD = { tittel: 120, beskrivelse: 3000, kommentar: 1000 };

const tidspunkt = (d) => new Date(d).toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' });
const kortDato = (d) => new Date(d).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });

const tomtSkjema = (side = '') => ({ type: '', tittel: '', beskrivelse: '', omrade: 'annet', viktighet: 'middels', side });

function Merke({ klasse, children }) {
  return <span className={`rounded-full px-2 py-0.5 text-sm font-semibold ${klasse}`}>{children}</span>;
}

// Valgknapper som radioknapper i en fieldset (tastatur og skjermleser fungerer som vanlig).
function Valg({ id, legend, verdier, valgt, onVelg, required }) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1 font-semibold text-slate-800">
        {legend}
        {required && (<><span aria-hidden="true" className="text-red-700"> *</span><span className="sr-only"> (må velges)</span></>)}
      </legend>
      <div className="flex flex-wrap gap-2">
        {Object.entries(verdier).map(([verdi, v]) => (
          <label
            key={verdi}
            className={`flex min-w-0 flex-1 basis-40 cursor-pointer items-start gap-2 rounded-xl border-2 bg-white p-2.5 focus-within:ring-4 focus-within:ring-indigo-600/50 ${valgt === verdi ? 'border-indigo-600 bg-indigo-50' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            <input
              type="radio"
              name={id}
              value={verdi}
              checked={valgt === verdi}
              onChange={() => onVelg(verdi)}
              required={required}
              className="mt-1 h-4 w-4 shrink-0 accent-indigo-700"
            />
            <span className="min-w-0">
              <span className="block font-semibold text-slate-900">{v.tekst}</span>
              {v.hjelp && <span className="block text-sm text-slate-600">{v.hjelp}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function NySkjema({ maks, startSide, onLagret }) {
  const [skjema, setSkjema] = useState(() => tomtSkjema(startSide));
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const endre = (endring) => {
    setSkjema((s) => ({ ...s, ...endring }));
    setStatus((st) => (st.type === 'feil' ? { type: '', tekst: '' } : st));
  };
  const felt = (navn) => (e) => endre({ [navn]: e.target.value });

  async function send(e) {
    e.preventDefault();
    if (!skjema.type) return setStatus({ type: 'feil', tekst: 'Velg om det er en feil, en forbedring eller en idé.' });
    setStatus({ type: 'sender', tekst: '' });
    try {
      const t = await kall('tilbakemeldinger', 'POST', skjema);
      onLagret(t);
      setSkjema(tomtSkjema());
      setStatus({ type: 'ok', tekst: `Takk! «${t.tittel}» er lagt i listen under.` });
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  const sender = status.type === 'sender';
  return (
    <form onSubmit={send} aria-labelledby="tb-ny" className="space-y-4 rounded-2xl border-2 border-slate-200 bg-slate-50 p-3">
      <h2 id="tb-ny" className="text-lg font-bold text-slate-900">Ny tilbakemelding</h2>
      <Valg id="tb-type" legend="Hva gjelder det?" verdier={TYPE} valgt={skjema.type} onVelg={(type) => endre({ type })} required />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-w-0 space-y-3">
          <Tekstfelt id="tb-tittel" etikett="Kort overskrift" required maxLength={maks.tittel}
            value={skjema.tittel} onChange={felt('tittel')} />
          <Tekstfelt id="tb-beskrivelse" etikett="Beskrivelse" flerlinjet rows={7} required maxLength={maks.beskrivelse}
            hjelp={skjema.type === 'feil'
              ? 'Hva gjorde du, hva skjedde, og hva skulle ha skjedd? Ikke skriv navn, telefon eller e-post til kunder.'
              : 'Hva er problemet i dag, og hvordan kunne det vært løst? Ikke skriv navn, telefon eller e-post til kunder.'}
            value={skjema.beskrivelse} onChange={felt('beskrivelse')} />
        </div>
        <div className="min-w-0 space-y-3">
          <div className="min-w-0">
            <label htmlFor="tb-omrade" className="mb-1 block font-semibold text-slate-800">Del av appen</label>
            <select id="tb-omrade" value={skjema.omrade} onChange={felt('omrade')} className={feltKlasse}>
              {Object.entries(OMRADE).map(([v, tekst]) => <option key={v} value={v}>{tekst}</option>)}
            </select>
          </div>
          <Tekstfelt id="tb-side" etikett="Side" maxLength={200}
            hjelp="Fylles inn med siden du kom fra. Tøm feltet hvis det gjelder noe annet."
            value={skjema.side} onChange={felt('side')} />
          <Valg id="tb-viktighet" legend="Hvor viktig er det?" verdier={VIKTIGHET} valgt={skjema.viktighet} onVelg={(viktighet) => endre({ viktighet })} />
        </div>
      </div>
      {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
      {status.type === 'ok' && <Melding type="ok">{status.tekst}</Melding>}
      <button type="submit" disabled={sender} className={knappPrimar}>
        <Ikon navn="send" className="w-5 h-5" /> {sender ? 'Sender …' : 'Send tilbakemelding'}
      </button>
    </form>
  );
}

function Kommentarer({ t, maks, onEndret }) {
  const [tekst, setTekst] = useState('');
  const [feil, setFeil] = useState('');
  const [sender, setSender] = useState(false);
  async function send(e) {
    e.preventDefault();
    if (!tekst.trim()) return;
    setSender(true);
    try {
      onEndret(await kall(`tilbakemeldinger/${t._id}/kommentarer`, 'POST', { tekst }));
      setTekst('');
      setFeil('');
    } catch (err) {
      setFeil(err.message);
    } finally {
      setSender(false);
    }
  }
  const antall = (t.kommentarer || []).length;
  const utviklersvar = (t.kommentarer || []).filter((k) => k.utvikler).length;
  return (
    <details className="min-w-0">
      <summary className="cursor-pointer font-semibold text-slate-800">
        Kommentarer ({antall}){utviklersvar ? ` · ${utviklersvar} fra utvikler` : ''}
      </summary>
      <div className="mt-2 space-y-2">
        {antall > 0 && (
          <ul className="space-y-2">
            {t.kommentarer.map((k) => (
              <li key={k._id} className={`rounded-xl p-2 ${k.utvikler ? 'border-l-4 border-indigo-600 bg-indigo-50' : 'bg-slate-50'}`}>
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-700">
                  <span>{k.navn} · {tidspunkt(k.dato)}</span>
                  {k.utvikler && <Merke klasse="bg-indigo-700 text-white">Utvikler</Merke>}
                  {k.status && <span>satte status til <strong>{STATUS[k.status]?.tekst}</strong></span>}
                </p>
                <p className="whitespace-pre-line text-slate-900 [overflow-wrap:anywhere]">{k.tekst}</p>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={send} className="space-y-2">
          <Tekstfelt id={`tb-k-${t._id}`} etikett="Ny kommentar" flerlinjet rows={2} maxLength={maks.kommentar}
            value={tekst} onChange={(e) => setTekst(e.target.value)} />
          {feil && <Melding type="feil">{feil}</Melding>}
          <button type="submit" disabled={sender || !tekst.trim()} className={knappSekundar}>
            {sender ? 'Lagrer …' : 'Legg til kommentar'}
          </button>
        </form>
      </div>
    </details>
  );
}

// Bare for utviklere: ny status og en melding til lederne, lagret sammen.
function Utviklerpanel({ t, maks, onEndret }) {
  const [status, setStatus] = useState(t.status);
  const [kommentar, setKommentar] = useState('');
  const [feil, setFeil] = useState('');
  const [lagrer, setLagrer] = useState(false);
  useEffect(() => { setStatus(t.status); }, [t.status]);
  const uendret = status === t.status && !kommentar.trim();

  async function lagre(e) {
    e.preventDefault();
    if (uendret) return;
    setLagrer(true);
    try {
      onEndret(await kall(`tilbakemeldinger/${t._id}/status`, 'PATCH', { status, kommentar }));
      setKommentar('');
      setFeil('');
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  return (
    <form onSubmit={lagre} aria-label={`Utvikler: «${t.tittel}»`} className="min-w-0 space-y-2 rounded-xl border-2 border-indigo-200 bg-indigo-50/60 p-2.5">
      <p className="font-semibold text-indigo-950">Utvikler</p>
      <div className="min-w-0">
        <label htmlFor={`tb-s-${t._id}`} className="mb-1 block text-sm font-semibold text-slate-800">Status</label>
        <select id={`tb-s-${t._id}`} value={status} onChange={(e) => setStatus(e.target.value)} className={`${feltKlasse} w-auto max-w-full`}>
          {Object.entries(STATUS).map(([v, s]) => <option key={v} value={v}>{s.tekst}{s.hjelp ? ` (${s.hjelp})` : ''}</option>)}
        </select>
      </div>
      <Tekstfelt id={`tb-uk-${t._id}`} etikett="Melding til lederne" flerlinjet rows={2} maxLength={maks.kommentar}
        hjelp="Valgfri. F.eks. hva som er gjort, hva som gjenstår eller hvorfor det ikke blir gjort."
        value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
      {feil && <Melding type="feil">{feil}</Melding>}
      <button type="submit" disabled={lagrer || uendret} className={knappPrimar}>
        <Ikon navn="hake" className="w-5 h-5" /> {lagrer ? 'Lagrer …' : 'Lagre'}
      </button>
    </form>
  );
}

function Statushistorikk({ logg }) {
  if ((logg || []).length < 2) return null;
  return (
    <details className="text-sm">
      <summary className="cursor-pointer font-semibold text-slate-800">Statushistorikk ({logg.length})</summary>
      <ol className="mt-1 space-y-0.5 text-slate-700">
        {[...logg].reverse().map((s, i) => (
          <li key={i}>{tidspunkt(s.dato)} · <strong>{STATUS[s.status]?.tekst}</strong> · {s.av}</li>
        ))}
      </ol>
    </details>
  );
}

function Kort({ t, meg, maks, erUtvikler, onEndret, onSlettet }) {
  const [feil, setFeil] = useState('');
  const [bekreft, setBekreft] = useState(false);
  const enig = (t.enige || []).some((e) => e.navn === meg);
  const antallEnige = (t.enige || []).length;

  async function utfor(sti, metode, data, ferdig = onEndret) {
    try {
      ferdig(await kall(sti, metode, data));
      setFeil('');
    } catch (err) {
      setFeil(err.message);
    }
  }
  const sisteStatus = (t.statusLogg || []).at(-1);

  return (
    <article id={`tb-kort-${t._id}`} tabIndex={-1} aria-labelledby={`tb-${t._id}`}
      className={`min-w-0 scroll-mt-4 space-y-2 rounded-2xl border-2 bg-white p-3 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${t.nyForMeg ? 'border-indigo-400' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Merke klasse={TYPE[t.type]?.merke}>{TYPE[t.type]?.tekst}</Merke>
        <Merke klasse={STATUS[t.status]?.merke}>{STATUS[t.status]?.tekst}</Merke>
        <Merke klasse={VIKTIGHET[t.viktighet]?.merke}>{VIKTIGHET[t.viktighet]?.tekst}</Merke>
        {t.nyForMeg > 0 && (
          <>
            <Merke klasse="bg-indigo-700 text-white">Nytt for deg ({t.nyForMeg})</Merke>
            <button type="button" onClick={() => utfor(`tilbakemeldinger/${t._id}/sett`, 'POST')}
              className="rounded-lg px-2 py-1 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
              Merk som sett<span className="sr-only">: {t.tittel}</span>
            </button>
          </>
        )}
      </div>
      <h3 id={`tb-${t._id}`} className="text-lg font-bold text-slate-900 [overflow-wrap:anywhere]">{t.tittel}</h3>
      <p className="text-sm text-slate-600 [overflow-wrap:anywhere]">
        {t.opprettetAv} · {kortDato(t.createdAt)} · {OMRADE[t.omrade] || 'Annet'}
        {t.side ? ` · ${t.side}` : ''}
        {t.butikk ? ` · ${t.butikk}` : ''}
      </p>
      <p className="whitespace-pre-line text-slate-900 [overflow-wrap:anywhere]">{t.beskrivelse}</p>
      {sisteStatus && t.status !== 'ny' && (
        <p className="text-sm text-slate-600">
          {STATUS[t.status]?.tekst}{STATUS[t.status]?.hjelp ? ` (${STATUS[t.status].hjelp})` : ''} · satt av {sisteStatus.av} {tidspunkt(sisteStatus.dato)}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <button
          type="button"
          aria-pressed={enig}
          onClick={() => utfor(`tilbakemeldinger/${t._id}/enig`, 'POST')}
          className={`${knappSekundar} ${enig ? 'border-indigo-600 bg-indigo-50 text-indigo-900' : ''}`}
        >
          <Ikon navn="hake" className="w-5 h-5" /> Enig ({antallEnige})
        </button>
        {t.opprettetAv === meg && t.status === 'ny' && !bekreft && (
          <button type="button" className={knappSekundar} onClick={() => setBekreft(true)}>Slett</button>
        )}
        {bekreft && (
          <span role="group" aria-label={`Slette «${t.tittel}»?`} className="flex flex-wrap items-center gap-2">
            <span className="text-slate-800">Slette den?</span>
            <button type="button" autoFocus className={`${knappSekundar} border-red-300 text-red-800`}
              onClick={() => { setBekreft(false); utfor(`tilbakemeldinger/${t._id}`, 'DELETE', undefined, () => onSlettet(t._id)); }}>
              Ja, slett
            </button>
            <button type="button" className={knappSekundar} onClick={() => setBekreft(false)}>Avbryt</button>
          </span>
        )}
      </div>
      {antallEnige > 0 && (
        <p className="text-sm text-slate-600 [overflow-wrap:anywhere]">Enige: {t.enige.map((e) => e.navn).join(', ')}</p>
      )}
      {feil && <Melding type="feil">{feil}</Melding>}
      <Statushistorikk logg={t.statusLogg} />
      {erUtvikler && <Utviklerpanel t={t} maks={maks} onEndret={onEndret} />}
      <Kommentarer t={t} maks={maks} onEndret={onEndret} />
    </article>
  );
}

export default function Tilbakemeldinger() {
  const leder = useErLeder();
  const utvikler = useErUtvikler();
  const tilgang = leder || utvikler;
  const location = useLocation();
  const [sok, setSok] = useSearchParams();
  // Fra backenden (den valgte ansatte er en av utviklerne), så statusvalget bare vises når det kan lagres.
  const [erUtvikler, setErUtvikler] = useState(false);
  const [liste, setListe] = useState(null);
  const [maks, setMaks] = useState(MAKS_STANDARD);
  const [feil, setFeil] = useState('');
  const [visning, setVisning] = useState('apne');
  const [type, setType] = useState('');
  const [omrade, setOmrade] = useState('');
  const [sortering, setSortering] = useState('nyeste');
  const meg = Cookies.get('selectedEmployee') || '';

  const hent = useCallback(async () => {
    if (!tilgang) return;
    try {
      const svar = await kall('tilbakemeldinger');
      setListe(svar.tilbakemeldinger);
      setErUtvikler(!!svar.erUtvikler);
      setMaks({ ...MAKS_STANDARD, ...svar.valg?.maks });
      setFeil('');
    } catch (err) {
      setFeil(err.message);
    }
  }, [tilgang]);
  useEffect(() => { hent(); }, [hent]);

  const erstatt = (ny) => setListe((l) => l.map((x) => (x._id === ny._id ? ny : x)));
  const erstattRef = useRef(erstatt);
  erstattRef.current = erstatt;

  // Fra varselet (?vis=<id>): vis innspillet, flytt fokus dit og merk det som sett for utvikleren.
  const vis = sok.get('vis');
  useEffect(() => {
    if (!vis || !liste) return;
    const t = liste.find((x) => x._id === vis);
    setSok({}, { replace: true });
    if (!t) return;
    setVisning('alle');
    setType('');
    setOmrade('');
    requestAnimationFrame(() => {
      const el = document.getElementById(`tb-kort-${vis}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el?.focus({ preventScroll: true });
    });
    if (t.nyForMeg) kall(`tilbakemeldinger/${vis}/sett`, 'POST').then(erstattRef.current).catch(() => {});
  }, [vis, liste, setSok]);

  const viste = useMemo(() => {
    const l = (liste || [])
      .filter(VISNING[visning].filter)
      .filter((t) => !type || t.type === type)
      .filter((t) => !omrade || t.omrade === omrade);
    if (sortering === 'enige') return [...l].sort((a, b) => (b.enige?.length || 0) - (a.enige?.length || 0));
    return l;
  }, [liste, visning, type, omrade, sortering]);
  const antall = (v) => (liste || []).filter(VISNING[v].filter).length;

  const fra = location.state?.fra;
  const startSide = typeof fra === 'string' && fra !== '/tilbakemeldinger' ? fra : '';

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp ikon="lapp" farge="leder" tittel="Tilbakemeldinger"
        undertittel="Meld feil, forbedringer og ideer til ansatt-appen. Si deg enig i det andre har skrevet, så vet vi hva som er viktigst. Utviklerne svarer og viser hvor langt hvert innspill har kommet." />

      {!tilgang && <Melding>Bare ledere og utviklere kan se tilbakemeldingene. Velg deg selv øverst i menyen.</Melding>}
      {feil && <Melding type="feil">{feil}</Melding>}

      {tilgang && (
        <>
          <NySkjema key={startSide} maks={maks} startSide={startSide} onLagret={(t) => { setListe((l) => [t, ...(l || [])]); setVisning('apne'); }} />

          <section aria-labelledby="tb-liste" className="space-y-3">
            <h2 id="tb-liste" className="text-lg font-bold text-slate-900">Innspill</h2>
            <div className="flex flex-wrap items-end gap-3">
              <div role="group" aria-label="Vis" className="flex flex-wrap gap-2">
                {Object.entries(VISNING).map(([v, { tekst }]) => (
                  <button key={v} type="button" aria-pressed={visning === v} onClick={() => setVisning(v)}
                    className={`${knappSekundar} ${visning === v ? 'border-indigo-600 bg-indigo-50 text-indigo-900' : ''}`}>
                    {tekst} ({antall(v)})
                  </button>
                ))}
              </div>
              <div className="min-w-0">
                <label htmlFor="tb-f-type" className="mb-1 block text-sm font-semibold text-slate-800">Type</label>
                <select id="tb-f-type" value={type} onChange={(e) => setType(e.target.value)} className={`${feltKlasse} w-auto`}>
                  <option value="">Alle</option>
                  {Object.entries(TYPE).map(([v, t]) => <option key={v} value={v}>{t.tekst}</option>)}
                </select>
              </div>
              <div className="min-w-0">
                <label htmlFor="tb-f-omrade" className="mb-1 block text-sm font-semibold text-slate-800">Del av appen</label>
                <select id="tb-f-omrade" value={omrade} onChange={(e) => setOmrade(e.target.value)} className={`${feltKlasse} w-auto max-w-full`}>
                  <option value="">Alle</option>
                  {Object.entries(OMRADE).map(([v, tekst]) => <option key={v} value={v}>{tekst}</option>)}
                </select>
              </div>
              <div className="min-w-0">
                <label htmlFor="tb-f-sort" className="mb-1 block text-sm font-semibold text-slate-800">Sorter</label>
                <select id="tb-f-sort" value={sortering} onChange={(e) => setSortering(e.target.value)} className={`${feltKlasse} w-auto`}>
                  <option value="nyeste">Nyeste først</option>
                  <option value="enige">Flest enige først</option>
                </select>
              </div>
            </div>

            {liste === null && !feil && <p className="text-slate-700">Laster …</p>}
            {liste !== null && !viste.length && (
              <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Ingen tilbakemeldinger her.</p>
            )}
            <ul className="space-y-3">
              {viste.map((t) => (
                <li key={t._id}>
                  <Kort t={t} meg={meg} maks={maks} erUtvikler={erUtvikler} onEndret={erstatt} onSlettet={(id) => setListe((l) => l.filter((x) => x._id !== id))} />
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
